import { create } from 'zustand';
import type { PlatformBridge } from '@slate/core';
import { useToastStore } from './toastStore';
import { useDocStore } from './docStore';

/**
 * Lifecycle of the in-app updater. The store is platform-agnostic; the
 * Tauri adapter (`apps/app/src/updaterController.ts`) attaches itself at
 * boot via {@link UpdateStore.attachController}. On web, no controller
 * attaches and the Settings panel renders an "updates aren't available"
 * empty state; the toast never fires.
 *
 * State transitions are documented in
 * `docs/superpowers/specs/2026-06-10-update-notification-design.md` §4.5.
 */
export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'upToDate'
  | 'available'
  | 'installing'
  | 'error';

export type InstallPhase = 'downloading' | 'installing' | 'restarting';

export interface UpdateController {
  /** Probe the configured endpoint. Returns `null` when up to date. */
  check(): Promise<{ version: string } | null>;
  /** Download, install, and relaunch. Never returns on success. */
  install(): Promise<void>;
}

export interface UpdateStore {
  status: UpdateStatus;
  currentVersion: string | null;
  latestVersion: string | null;
  lastCheckedAt: number | null;
  installPhase: InstallPhase | null;
  error: string | null;
  controllerAttached: boolean;
  /** Id of the sticky "update available" toast we pushed for this
   *  `available` transition, if any. Used to dismiss the toast when the
   *  store leaves the `available` state by any path. */
  pendingToastId: number | null;

  attachController: (c: UpdateController) => void;
  loadCurrentVersion: (p: PlatformBridge) => Promise<void>;
  /** Manually check for updates. The `mode` parameter governs both toast
   *  behavior and error visibility (spec §4.6). */
  checkNow: (opts?: { mode?: 'startup' | 'manual' }) => Promise<void>;
  /** "[Later]" toast action: write `latestVersion` to localStorage and
   *  clear it from the store. */
  dismissPending: () => void;
  /** Entry point from any [Update now] surface. Reads dirty docs from
   *  the docStore. If any are dirty, dispatches OPEN_UPDATE_GATE_EVENT
   *  for the App to open the save-all dialog. Otherwise proceeds straight
   *  to {@link confirmInstall}. */
  requestInstall: (platform: PlatformBridge) => Promise<void>;
  /** Called after the gate confirms (or skipped when no dirty docs).
   *  Transitions to `installing` and runs the controller. */
  confirmInstall: () => Promise<void>;
}

export const DISMISSED_KEY = 'slate:updates:dismissedVersion';

/** Window event name the sticky update toast dispatches from its
 *  `Update now` action. The App shell listens for this and routes to
 *  the install flow (which needs the PlatformBridge the store can't see). */
export const START_UPDATE_EVENT = 'slate:start-update';

/** Window event the store dispatches when an install was requested but
 *  some docs are dirty. The App listens and opens the save-all dialog. */
export const OPEN_UPDATE_GATE_EVENT = 'slate:open-update-gate';

let _controller: UpdateController | null = null;

export const useUpdateStore = create<UpdateStore>((set, get) => ({
  status: 'idle',
  currentVersion: null,
  latestVersion: null,
  lastCheckedAt: null,
  installPhase: null,
  error: null,
  controllerAttached: false,
  pendingToastId: null,

  attachController: (c) => {
    _controller = c;
    set({ controllerAttached: true });
  },

  loadCurrentVersion: async (platform) => {
    if (typeof platform.getAppVersion !== 'function') return;
    try {
      const v = await platform.getAppVersion();
      set({ currentVersion: v });
    } catch {
      /* leave null; About and Settings handle the unknown case */
    }
  },

  // TODO(spec §4.5): single in-flight check assumed. If overlapping checks become reachable (e.g. periodic re-checks land), guard the trailing `set` with a sequence token.
  checkNow: async (opts) => {
    const mode = opts?.mode ?? 'manual';
    const controller = getController();
    if (!controller) return; // web target, no-op
    set({ status: 'checking' });
    let result: { version: string } | null;
    try {
      result = await controller.check();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (mode === 'startup') {
        // Startup probe stays invisible on failure.
        set({ status: 'idle' });
      } else {
        set({ status: 'error', error: message, lastCheckedAt: Date.now() });
      }
      return;
    }
    const checkedAt = Date.now();
    if (result === null) {
      set({ status: 'upToDate', lastCheckedAt: checkedAt });
      return;
    }
    set({
      status: 'available',
      latestVersion: result.version,
      lastCheckedAt: checkedAt,
    });
    if (mode !== 'startup') return;
    // Suppress the toast if the user has previously deferred this exact
    // version. They can still install it via Settings, Update now.
    let dismissed: string | null = null;
    try {
      dismissed = globalThis.localStorage?.getItem(DISMISSED_KEY) ?? null;
    } catch {
      /* private-mode safari */
    }
    if (dismissed === result.version) return;
    const cur = get().currentVersion;
    const sub = cur ? ` You're on v${cur}.` : '';
    const message = `Slate v${result.version} is available.${sub}`;
    const toastId = useToastStore.getState().push(message, {
      sticky: true,
      actions: [
        {
          label: 'Update now',
          primary: true,
          onClick: () => {
            // Dismiss the toast immediately; the install flow takes over.
            const id = get().pendingToastId;
            if (id !== null) useToastStore.getState().dismiss(id);
            set({ pendingToastId: null });
            // The actual install needs a PlatformBridge; the toast action
            // dispatches a window event the App listens for, since the
            // store can't see the bridge here.
            window.dispatchEvent(new CustomEvent(START_UPDATE_EVENT));
          },
        },
        {
          label: 'Later',
          onClick: () => {
            get().dismissPending();
          },
        },
      ],
    });
    set({ pendingToastId: toastId });
  },

  dismissPending: () => {
    const { latestVersion: v, pendingToastId } = get();
    if (pendingToastId !== null) {
      useToastStore.getState().dismiss(pendingToastId);
    }
    if (v) {
      try {
        globalThis.localStorage?.setItem(DISMISSED_KEY, v);
      } catch {
        /* private-mode safari best-effort persistence */
      }
    }
    set({
      status: 'idle',
      latestVersion: null,
      pendingToastId: null,
      error: null,
    });
  },

  requestInstall: async (_platform) => {
    if (!getController()) return;
    if (get().status !== 'available') return;
    // Drop the toast immediately so the user doesn't see stale state.
    const toastId = get().pendingToastId;
    if (toastId !== null) {
      useToastStore.getState().dismiss(toastId);
      set({ pendingToastId: null });
    }
    const dirty = useDocStore.getState().docs.filter((d) => d.dirty).length;
    if (dirty > 0) {
      window.dispatchEvent(
        new CustomEvent(OPEN_UPDATE_GATE_EVENT, { detail: { dirty } }),
      );
      return;
    }
    await get().confirmInstall();
  },

  confirmInstall: async () => {
    const controller = getController();
    if (!controller) return;
    set({ status: 'installing', installPhase: 'downloading', error: null });
    try {
      await controller.install();
      // Phase progression for the modal. install() returns just before
      // relaunch() in the controller, so the intermediate phases are
      // approximations meant to show motion; the spec accepts the brief
      // flicker as the trade for not wiring per-byte progress events.
      set({ installPhase: 'installing' });
      set({ installPhase: 'restarting' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set({ status: 'error', error: message, installPhase: null });
    }
  },
}));

/** Internal accessor for the attached controller. Exported for the
 *  Tauri-side test harness and for `checkNow` / `confirmInstall` to call.
 *  Not part of the public store API. */
export function getController(): UpdateController | null {
  return _controller;
}

/** Reset hook for tests. Clears the module-private controller pointer
 *  so cross-file leakage in the same Vitest worker can't make later
 *  tests see a stale `getController()`. Do NOT call from production code. */
export function __resetControllerForTests(): void {
  _controller = null;
}
